import { useEffect, useMemo } from 'react';
import { ca } from '@cian/analytics';
import { useLocation } from 'react-router-dom';
import { useDeviceType } from '@cian/ui-kit';
import { useDispatch, useSelector } from 'react-redux';

import {
  combineDataLayerObjectNew,
  ICombineDataLayerObjectParamsPage,
} from '../../../app/helpers/combine_data_layer_object';
import { selectAnalytics } from '../../selectors/analytics';
import { useCurrentBreadCrumbs } from '../useCurrentBreadCrumbs';
import { setInitialPageNumber } from '../../actions/settings';
import { preparePageNumber } from '../preparePageNumber';
import { selectInitialPageNumber } from '../../selectors/settings/selectInitialPageNumber';
import { prepareListingOrdering } from '../prepareListingOrdering';

/**
 * Трэкает аналитику для листингов
 */
export const useListingAnalytics = ({ page }: { page?: Partial<ICombineDataLayerObjectParamsPage> } = {}) => {
  const { search } = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);
  const qsPage = preparePageNumber(searchParams.get('page') as string);
  const qsOrdering = prepareListingOrdering(searchParams.get('ordering') || '') || '';
  const deviceType = useDeviceType();
  const analytics = useSelector(selectAnalytics);
  const breadCrumbs = useCurrentBreadCrumbs();
  const dispatch = useDispatch();
  const initialPageNumber = useSelector(selectInitialPageNumber);

  useEffect(() => {
    /**
     * Исключение при сбросе номера страницы
     * (например, находясь на 5 странице, кликнули на ту же рубрику в меню и попали на 1-ую)
     * В этом случае не трекаем сразу, а сбрасываем initialPageNumber
     * и ждем вторичной отработки useEffect
     */
    if (qsPage === 1 && initialPageNumber !== 1) {
      dispatch(setInitialPageNumber(qsPage));

      return;
    }

    const breadCrumbNames = breadCrumbs.map(breadCrumb => breadCrumb.breadCrumbName);
    const params = combineDataLayerObjectNew(analytics, {
      page: {
        breadCrumbs: breadCrumbNames,
        siteType: deviceType === 'phone' ? 'mobile' : deviceType,
        ...(page || {}),
        extra: page?.extra,
      },
    });

    /**
     * В общем случае трекаем событие newpage
     * - заход на страницу
     * - смена номера страницы
     * - клик по тегу (with-tag) на Главной
     * - смена сортировки страницы
     */
    if (initialPageNumber === qsPage) {
      ca('pageviewSite', params);
    } else {
      // Если подгружаем статьи, то отправляем событие newpage_infinite_search
      ca('event', {
        name: 'oldevent',
        category: 'page',
        action: 'newpage_infinite_search',
        label: '',
        ...params,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analytics, breadCrumbs, deviceType, qsPage, initialPageNumber, dispatch, qsOrdering]);
};
